import type { FC } from 'react';
import React, { useEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type {
	EditorTypeAndVersionPropertyQueryType,
	EditorTypeAndVersionPropertyQueryVariablesType,
} from '@confluence/load-edit-page/entry-points/EditorTypeAndVersionPropertyQuery.ts';
import { EditorTypeAndVersionPropertyQuery } from '@confluence/load-edit-page/entry-points/EditorTypeAndVersionPropertyQuery.graphql.ts';
import {
	extractEditorContentType,
	extractIsEditorVersionFabric,
} from '@confluence/load-edit-page/entry-points/editorTypeAndVersionPropertyHelpers.ts';
import { NativeEditPageDataLoader } from '@confluence/load-edit-page/entry-points/NativeEditPageDataLoader.ts';
import type { IEditorPreloader } from '@confluence/edit-button/entry-points/types.ts';
import {
	isJSOnlyPreloadEditSources,
	isLoadingFromCreate,
} from '@confluence/load-edit-page/entry-points/checkEditSourceType.ts';
import { useEditSource } from '@confluence/edit-common/entry-points/useEditSource.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { nativeEditorComponentsPreloader, tinyEditorComponentsPreloader } from './lib/index.ts';
import { TinyEditPageDataLoaderLoader } from './components/TinyEditPageDataLoader/index.ts';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

/**
 * Preloads editor components and data
 * See also {@see EditPageLoadingComponent}
 */
const Preloader: FC<IEditorPreloader> = ({ contentId, spaceKey, isEmbeddedEditor }) => {
	const preloadTimingStart = performance.now();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [{ editSource }] = useEditSource();
	const isCreate = isLoadingFromCreate(editSource);
	const isEditPreloadJSOnly = isJSOnlyPreloadEditSources(editSource);

	const skipEditorTypeAndVersionQuery = isEmbeddedEditor || isCreate || !contentId;

	const {
		data: isEditorVersionFabricData,
		loading: isEditorVersionFabricLoading,
		error: isEditorVersionFabricError,
	} = useQueryInterop<
		EditorTypeAndVersionPropertyQueryType,
		EditorTypeAndVersionPropertyQueryVariablesType
	>(EditorTypeAndVersionPropertyQuery, {
		variables: {
			contentId,
		},
		skip: skipEditorTypeAndVersionQuery,
	});

	// Finished takes into account if query is done loading or if it was skipped.
	const editorTypeAndVersionQueryFinished =
		skipEditorTypeAndVersionQuery || !isEditorVersionFabricLoading;

	let isFabricEditor: boolean = true;
	let contentType: string | null = null;

	// Skip extracting isFabricEditor + default to Fabric editor if query was skipped or is loading.
	if (!skipEditorTypeAndVersionQuery && !isEditorVersionFabricLoading) {
		isFabricEditor =
			!!isEditorVersionFabricError || extractIsEditorVersionFabric(isEditorVersionFabricData);
		contentType = !!isEditorVersionFabricError
			? contentType
			: extractEditorContentType(isEditorVersionFabricData);
	}

	const fireTimingAnalytics = () => {
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				actionSubject: 'editor',
				action: 'preloader.timing',
				source: 'editorPreloader',
				attributes: {
					timing: performance.now() - preloadTimingStart,
					collabService: 'ncs',
					isFabricEditor,
					contentId,
				},
			},
		}).fire();
	};

	useEffect(() => {
		// internally pubsub-client will only initialize once
		void import(
			/* webpackChunkName: "loadable-confluencepubsub-cliententry-pointsclient" */ '@confluence/pubsub-client/entry-points/client.ts'
		).then(({ getPubSubClient }) => getPubSubClient());

		// Preload all edit page loadables in the background when edit page preloading triggers.
		if (expValEquals('cc_editor_edit_page_preload_loadables', 'isEnabled', true)) {
			void import(
				/* webpackChunkName: "loadable-editor-loadables-preloader" */
				'@confluence/editor-loadables-preloader/entry-points/preloadEditPageLoadables.ts'
			).then(({ preloadEditPageLoadables }) => preloadEditPageLoadables());
		}

		if (editorTypeAndVersionQueryFinished) {
			if (!isFabricEditor) {
				void tinyEditorComponentsPreloader().then(() => {
					fireTimingAnalytics();
				});
			} else {
				void nativeEditorComponentsPreloader().then(() => {
					fireTimingAnalytics();
				});
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isEditorVersionFabricData, isFabricEditor]);

	if (isCreate || !contentId || isEditPreloadJSOnly) {
		// Don't preload editor data when it is create or contentId is not available
		return null;
	}

	if (editorTypeAndVersionQueryFinished && !isFabricEditor) {
		return <TinyEditPageDataLoaderLoader contentId={contentId} contentType={contentType} />;
	} else if (editorTypeAndVersionQueryFinished) {
		// NativeEditPageDataLoader is NCS specific (loads NCS queries only)
		return (
			<NativeEditPageDataLoader
				contentId={contentId}
				spaceKey={spaceKey}
				contentType={contentType}
			/>
		);
	} else {
		return null;
	}
};

/**
 * Preloads editor resources and data when user intent to navigate
 * to the edit page is already known.
 *
 * @param contentId - content Id
 * @param spaceKey - space key
 * @param editSource - where this preload request is from
 */
export const getEditorPreloader = () => {
	return (config: IEditorPreloader): React.JSX.Element => {
		return <Preloader {...config} />;
	};
};
